import React from 'react';
import { Button } from './Button';
import { Modal } from './Modal';

export type ConfirmationModalVariant = 'danger' | 'warning' | 'success' | 'primary';

export interface ConfirmationDetail {
  label: string;
  value: string;
}

export interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  variant?: ConfirmationModalVariant;
  details?: ConfirmationDetail[];
  error?: string | null;
  successMessage?: string | null;
  additionalContent?: React.ReactNode;
  modalClassName?: string;
  preventCloseOnOverlayClick?: boolean;
  confirmDisabled?: boolean;
}

/**
 * ConfirmationModal - A reusable confirmation dialog component
 * 
 * Supports:
 * - Custom title and message
 * - Optional details (key-value pairs)
 * - Loading states
 * - Error and success messages
 * - Variant-based button styling
 * - Additional content (e.g., lists, warnings)
 * 
 * @example
 * <ConfirmationModal
 *   isOpen={showConfirm}
 *   title="Mark Referral as Complete"
 *   message="Are you sure you want to mark this referral as completed?"
 *   confirmLabel="Mark Complete"
 *   onConfirm={handleConfirm}
 *   onCancel={handleCancel}
 *   isLoading={completing}
 *   variant="success"
 *   details={[
 *     { label: 'Patient', value: 'John Doe' },
 *     { label: 'Referral ID', value: 'REF123' }
 *   ]}
 * />
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  variant = 'primary',
  details = [],
  error,
  successMessage,
  additionalContent,
  modalClassName = '',
  preventCloseOnOverlayClick = false,
  confirmDisabled = false,
}) => {
  const getConfirmButtonVariant = (): 'primary' | 'success' | 'danger' | 'warning' => {
    switch (variant) {
      case 'danger':
        return 'danger';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      default:
        return 'primary';
    }
  };

  return (
    <Modal 
      isOpen={isOpen}
      onClose={onCancel}
      isLoading={isLoading}
      className={modalClassName}
      preventCloseOnOverlayClick={preventCloseOnOverlayClick}
    >
      <div className="modal-header">
        <h3>{title}</h3>
      </div>
      <div className="modal-body">
        {typeof message === 'string' ? <p>{message}</p> : message}
        
        {additionalContent && (
          <div style={{ marginTop: '12px' }}>
            {additionalContent}
          </div>
        )}

        {details.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            {details.map((detail, index) => (
              <p key={index}>
                <strong>{detail.label}:</strong> {detail.value}
              </p>
            ))}
          </div>
        )}

        {error && (
          <p style={{ color: '#f44336', marginTop: '12px' }}>
            {error}
          </p>
        )}

        {successMessage && (
          <p style={{ 
            color: successMessage.startsWith('✓') ? '#4caf50' : '#f44336', 
            marginTop: '12px' 
          }}>
            {successMessage}
          </p>
        )}
      </div>
      <div className="modal-footer">
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          {cancelLabel}
        </Button>
        <Button
          variant={getConfirmButtonVariant()}
          onClick={onConfirm}
          disabled={isLoading || confirmDisabled}
          isLoading={isLoading}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
};

